// Campo de data em formato brasileiro (dd/mm/aaaa) — input nativo type="date" exibe
// mm/dd/yyyy em navegadores com locale en-US independentemente do atributo lang.
// Aqui o usuário digita dd/mm/aaaa; o botão de calendário abre o seletor nativo do
// navegador via showPicker() num input type="date" oculto. O componente emite o valor
// em ISO (yyyy-mm-dd) via onChange, com a mesma forma de evento (e.target.value) de um
// input nativo.
import { useState, useEffect, useRef } from "react";

function maskDigits(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  if (digits.length > 2) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return digits;
}

function isoToBr(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function brToIso(br) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br);
  if (!m) return "";
  const [, d, mo, y] = m;
  const day = Number(d);
  const month = Number(mo);
  const year = Number(y);
  const date = new Date(year, month - 1, day);
  const valid =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;
  return valid ? `${y}-${mo}-${d}` : "";
}

const inputStyle = {
  width: "100%",
  padding: "16px 52px 16px 28px",
  borderRadius: 40,
  border: "none",
  background: "#e8e8e8",
  fontSize: 16,
  fontFamily: "'DM Sans', sans-serif",
  color: "#666",
  outline: "none",
  boxSizing: "border-box",
};

function DateInput({
  value,
  onChange,
  onKeyDown,
  placeholder = "dd/mm/aaaa",
  style: extraStyle,
  containerStyle,
}) {
  const [text, setText] = useState(isoToBr(value));
  const lastEmitted = useRef(value);
  const pickerRef = useRef(null);

  // Só resincroniza o texto a partir de "value" quando a mudança veio de fora
  // (reset do formulário, carga inicial, etc.) — evita apagar o que o usuário
  // está digitando quando uma data incompleta emite valor vazio para o pai.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setText(isoToBr(value));
      lastEmitted.current = value;
    }
  }, [value]);

  const emit = (iso) => {
    lastEmitted.current = iso;
    onChange({ target: { value: iso } });
  };

  const handleChange = (e) => {
    const masked = maskDigits(e.target.value);
    setText(masked);
    emit(brToIso(masked));
  };

  const handlePickerChange = (e) => {
    const iso = e.target.value;
    setText(isoToBr(iso));
    emit(iso);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        // segue para o fallback abaixo
      }
    }
    el.focus();
    el.click();
  };

  return (
    <div style={{ position: "relative", ...containerStyle }}>
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        maxLength={10}
        style={{
          ...inputStyle,
          ...extraStyle,
          paddingRight: Math.max(44, extraStyle?.paddingRight || 0),
        }}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Abrir calendário"
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#999"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="3" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      </button>
      {/* input nativo oculto, acionado programaticamente pelo botão acima */}
      <input
        ref={pickerRef}
        type="date"
        value={value || ""}
        onChange={handlePickerChange}
        tabIndex={-1}
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
}

export default DateInput;
